import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';
import { 
  FolderPlus,
  Folder,
  Users,
  Calendar,
  Clock,
  FileText,
  Upload,
  Download,
  ArrowLeft,
  Plus,
  Settings,
  BarChart3,
  Target,
  CheckCircle,
  AlertCircle,
  User,
  Languages,
  Files,
  Trash2,
  Edit2
} from 'lucide-react';
import StorageIndicator from '../components/StorageIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Projects = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [allResearchers, setAllResearchers] = useState([]);

  // Check authentication on mount
  useEffect(() => {
    if (!user) {
      toast({
        title: t('loginRequired'),
        description: t('loginRequiredMessage'),
        variant: "destructive"
      });
      navigate('/');
    } else {
      fetchMyProfile();
      fetchProjects();
    }
  }, [user, navigate, toast]);

  const fetchMyProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/`);
      if (response.ok) {
        const profiles = await response.json();
        const my = profiles.find(p => p.email === user.email);
        setMyProfile(my);
        setAllResearchers(profiles);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchProjects = async () => {
    if (!myProfile) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/?user_id=${myProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch projects when profile is loaded
  useEffect(() => {
    if (myProfile) {
      fetchProjects();
    }
  }, [myProfile]);

  const deleteProject = async (projectId, e) => {
    e.stopPropagation(); // Prevent card click event
    
    if (!window.confirm(t('confirmDeleteMessage'))) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}?user_id=${myProfile.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('projectDeleted'),
          description: t('projectDeletedMessage'),
        });
        fetchProjects(); // Refresh the list
      } else {
        const error = await response.json();
        throw new Error(error.detail || t('failedToCreateProject'));
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: t('deleteFailed'),
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleEditProject = (project, e) => {
    e.stopPropagation(); // Prevent card click event
    setEditingProject(project);
    setProjectForm({
      title: project.title || '',
      description: project.description || '',
      research_area: project.research_area || '',
      start_date: project.start_date || '',
      expected_end_date: project.expected_end_date || ''
    });
    setEditProjectOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    
    if (!myProfile || !editingProject) {
      toast({
        title: t('errorTitle'),
        description: t('createProfileFirst'),
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${editingProject.id}?user_id=${myProfile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(projectForm)
      });

      if (response.ok) {
        toast({
          title: t('projectUpdated'),
          description: t('projectUpdatedMessage'),
        });
        setEditProjectOpen(false);
        setEditingProject(null);
        setProjectForm({
          title: '',
          description: '',
          research_area: '',
          start_date: '',
          expected_end_date: ''
        });
        fetchProjects();
      } else {
        const error = await response.json();
        throw new Error(error.detail || t('failedToUpdateProject'));
      }
    } catch (error) {
      console.error('Error updating project:', error);
      toast({
        title: t('updateFailed'),
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const [projectForm, setProjectForm] = useState({
    title: '',
    description: '',
    research_area: '',
    start_date: '',
    expected_end_date: ''
  });

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (!myProfile) {
      toast({
        title: t('errorTitle'),
        description: t('createProfileFirst'),
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...projectForm,
          owner_id: myProfile.id,
          owner_name: myProfile.name
        })
      });

      if (response.ok) {
        toast({
          title: t('projectCreatedSuccessTitle'),
          description: t('projectReadyMessage'),
        });
        setProjectForm({
          title: '',
          description: '',
          research_area: '',
          start_date: '',
          expected_end_date: ''
        });
        setCreateProjectOpen(false);
        fetchProjects();
      } else {
        // Check if it's a subscription limit error
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || t('failedToCreateProject');
        
        // Special handling for subscription limit
        if (response.status === 403 && (errorMessage.includes('Basic plan') || errorMessage.includes('Premium plan'))) {
          toast({
            title: t('subscriptionLimitReached') || 'Subscription Limit Reached',
            description: errorMessage,
            variant: "destructive",
            duration: 6000
          });
        } else {
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      toast({
        title: t('errorTitle'),
        description: error.message || t('failedToCreateProject'),
        variant: "destructive"
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'planning': 'bg-blue-100 text-blue-700',
      'active': 'bg-green-100 text-green-700',
      'on_hold': 'bg-yellow-100 text-yellow-700',
      'completed': 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Format with time
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    if (diffDays === 1) return `Today at ${timeStr}`;
    if (diffDays === 2) return `Yesterday at ${timeStr}`;
    if (diffDays < 7) return `${diffDays} days ago at ${timeStr}`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  // Calculate dynamic progress based on tasks
  const calculateProgress = (project) => {
    if (!project.tasks || project.tasks.length === 0) return 0;
    const completedTasks = project.tasks.filter(t => t.status === 'completed').length;
    return Math.round((completedTasks / project.tasks.length) * 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_knowledgelink-1/artifacts/8zfj25zj_IMG_6196.png" 
                alt="RCP Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">RCP</span>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="flex items-center gap-2"
              >
                <Languages className="h-4 w-4" />
                {language === 'en' ? 'العربية' : 'English'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('home')}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Folder className="h-10 w-10 text-cyan-600" />
                <h1 className="text-4xl font-bold text-gray-900">{t('myProjectsTitle')}</h1>
              </div>
              <p className="text-lg text-gray-600">
                {t('createFirstProject')}
              </p>
            </div>
            
            <div className="text-right">
              {myProfile && (myProfile.subscription_plan === 'basic' || myProfile.subscription_plan === 'premium') && (
                <p className="text-sm text-gray-600 mb-2">
                  {t('projectsCreated') || 'Projects'}: {projects.filter(p => p.owner_id === myProfile.id).length}/{myProfile.subscription_plan === 'basic' ? '3' : '10'} 
                  <span className="text-xs ml-1">
                    ({myProfile.subscription_plan === 'basic' ? (t('basicPlan') || 'Basic Plan') : (t('premiumPlan') || 'Premium Plan')})
                  </span>
                </p>
              )}
              <Dialog open={createProjectOpen} onOpenChange={setCreateProjectOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white">
                    <FolderPlus className="mr-2 h-5 w-5" />
                    {t('createNewProject')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{t('createNewProject')}</DialogTitle>
                    <DialogDescription>
                      {myProfile && (myProfile.subscription_plan === 'basic' || myProfile.subscription_plan === 'premium') ? (
                        <>
                          {t('createFirstProject')}
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                            ⚠️ {myProfile.subscription_plan === 'basic' 
                              ? (t('basicPlanLimit') || 'Basic plan: Maximum 3 projects. Upgrade for more projects.')
                              : (t('premiumPlanLimit') || 'Premium plan: Maximum 10 projects. Upgrade to Advanced for unlimited projects.')
                            }
                          </div>
                        </>
                      ) : (
                        t('createFirstProject')
                      )}
                    </DialogDescription>
                  </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">{t('projectTitle')} *</Label>
                    <Input 
                      id="title" 
                      placeholder={t('projectTitlePlaceholder')}
                      value={projectForm.title}
                      onChange={(e) => setProjectForm({...projectForm, title: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">{t('projectDescription')}</Label>
                    <Textarea 
                      id="description" 
                      placeholder={t('projectDescription')}
                      value={projectForm.description}
                      onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="research_area">{t('researchField')}</Label>
                    <Input 
                      id="research_area" 
                      placeholder={t('describeResearchField')}
                      value={projectForm.research_area}
                      onChange={(e) => setProjectForm({...projectForm, research_area: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_date">{t('startDate')}</Label>
                      <Input 
                        id="start_date" 
                        type="date"
                        value={projectForm.start_date}
                        onChange={(e) => setProjectForm({...projectForm, start_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expected_end_date">{t('endDate')}</Label>
                      <Input 
                        id="expected_end_date" 
                        type="date"
                        value={projectForm.expected_end_date}
                        onChange={(e) => setProjectForm({...projectForm, expected_end_date: e.target.value})}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600">
                    {t('createProject')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>

            {/* Edit Project Dialog */}
            <Dialog open={editProjectOpen} onOpenChange={setEditProjectOpen}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('editProject')}</DialogTitle>
                  <DialogDescription>
                    {t('editProjectDescription')}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpdateProject} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-title">{t('projectTitle')} *</Label>
                    <Input 
                      id="edit-title" 
                      placeholder={t('projectTitlePlaceholder')}
                      value={projectForm.title}
                      onChange={(e) => setProjectForm({...projectForm, title: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-description">{t('projectDescription')}</Label>
                    <Textarea 
                      id="edit-description" 
                      placeholder={t('projectDescription')}
                      value={projectForm.description}
                      onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-research_area">{t('researchField')}</Label>
                    <Input 
                      id="edit-research_area" 
                      placeholder={t('describeResearchField')}
                      value={projectForm.research_area}
                      onChange={(e) => setProjectForm({...projectForm, research_area: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-start_date">{t('startDate')}</Label>
                      <Input 
                        id="edit-start_date" 
                        type="date"
                        value={projectForm.start_date}
                        onChange={(e) => setProjectForm({...projectForm, start_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-expected_end_date">{t('endDate')}</Label>
                      <Input 
                        id="edit-expected_end_date" 
                        type="date"
                        value={projectForm.expected_end_date}
                        onChange={(e) => setProjectForm({...projectForm, expected_end_date: e.target.value})}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600">
                    {t('updateProject')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Project Statistics */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('myProjects')}</CardTitle>
                <Folder className="h-4 w-4 text-cyan-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{projects.length}</div>
                <p className="text-xs text-gray-500 mt-1">{t('allYourProjects')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('activeProjects')}</CardTitle>
                <Target className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {projects.filter(p => {
                    const status = p.status?.toLowerCase() || '';
                    return status === 'active' || status === 'in-progress' || status === 'in_progress' || status === 'ongoing' || status === 'planning';
                  }).length}
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('currentlyInProgress')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('completed')}</CardTitle>
                <CheckCircle className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {projects.filter(p => {
                    const status = p.status?.toLowerCase() || '';
                    return status === 'completed' || status === 'done' || status === 'finished';
                  }).length}
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('successfullyFinished')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('collaborations')}</CardTitle>
                <Users className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {projects.filter(p => p.owner_id !== myProfile?.id).length}
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('projectsYouJoined')}</p>
              </CardContent>
            </Card>
          </div>

          {/* Storage Indicator */}
          <div className="mb-8">
            <StorageIndicator refreshTrigger={projects.length} />
          </div>

          {/* Projects List */}
          <div className="space-y-6">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">{t('loading')}...</p>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                <Folder className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('noProjectsYet')}</h3>
                <p className="text-gray-600 mb-4">{t('createFirstProject')}</p>
                <Button 
                  onClick={() => setCreateProjectOpen(true)}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  <FolderPlus className="mr-2 h-4 w-4" />
                  {t('createProject')}
                </Button>
              </div>
            ) : (
              <div className="grid gap-6">
                {projects.map((project) => (
                  <Card 
                    key={project.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <CardTitle className="text-xl">{project.title}</CardTitle>
                            <Badge className={getStatusColor(project.status)}>
                              {t(project.status) || project.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                          </div>
                          {project.description && (
                            <p className="text-gray-600 mb-3">{project.description}</p>
                          )}
                          {project.research_area && (
                            <Badge variant="outline" className="mb-3">
                              {project.research_area}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {project.owner_id === myProfile?.id && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={(e) => handleEditProject(project, e)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title={t('editProject')}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={(e) => deleteProject(project.id, e)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title={t('deleteProject')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button variant="outline" size="sm">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">{t('progress')}</span>
                          <span className="text-sm text-gray-500">{calculateProgress(project)}%</span>
                        </div>
                        <Progress 
                          value={calculateProgress(project)} 
                          className="h-2"
                          indicatorColor={
                            calculateProgress(project) === 100
                              ? 'bg-green-500'
                              : calculateProgress(project) > 0
                              ? 'bg-blue-500'
                              : 'bg-amber-500'
                          }
                        />
                        {project.tasks && project.tasks.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {project.tasks.filter(t => t.status === 'completed').length} {t('of')} {project.tasks.length} {t('tasksCompleted')}
                            {calculateProgress(project) === 100 && (
                              <span className="ml-2 text-green-600 font-semibold">✓ {t('completed')}!</span>
                            )}
                          </p>
                        )}
                      </div>

                      {/* Project Info Grid */}
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">
                            {t('owner')}: {project.owner_id === myProfile?.id ? t('you') : project.owner_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">
                            {project.collaborators?.length || 0} {t('collaboratorCount')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Files className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">
                            {project.files?.length || 0} {t('fileCount')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">
                            {t('lastAccess')}: {getTimeAgo(project.last_accessed)}
                          </span>
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t text-sm">
                        <div>
                          <span className="text-gray-500">{t('startDate')}: </span>
                          <span className="text-gray-700">{formatDate(project.start_date)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('endDate')}: </span>
                          <span className="text-gray-700">{formatDate(project.expected_end_date)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Projects;